import React from 'react';

import { defaultSignatureFieldHeightPx, isSignatureFieldElement, Select } from '@aline/common';
import { useReactiveVar } from '@apollo/client/react';
import { Typography } from '@mui/material';
import { equals } from 'ramda';
import { Editor } from 'slate';
import { AlineAvatar, AlineButton, AlineTooltip } from 'src/components';
import { useSyncAnnotations } from 'src/components/viewer/hooks';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import {
  applyPDFSignatureStamp,
  clearPDFSignatureStamp,
  findPDFSignatureWidgetByFieldId,
  signatureB64Var,
} from 'src/components/viewer/viewer-hooks';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useEditorTabStateSelectorForTabId } from 'src/editor/editor-tab-state-registry';
import { useMobileSheetActionButtonStyles } from 'src/editor/mobile/signing/mobile-sheet-action-button';
import { MobileAdoptAndSignBody } from 'src/editor/mobile/signing/MobileAdoptAndSignBody';
import { MobileSheetActionFooter } from 'src/editor/mobile/signing/MobileSheetActionFooter';
import { useGetFieldDisabledReason } from 'src/editor/popovers/fields/helpers';
import { useGetUserSignatureImg } from 'src/editor/popovers/fields/SignHerePopoverContent';
import type { Apollo } from 'src/gql/generated';
import { getAssigneeDisplayDetails } from 'src/helpers';
import { useAuthorizedTransforms, useGetCurrentDoc } from 'src/hooks';
import type { AdoptedSignature } from 'src/layout-engine/components/dialogs/use-adopt-user-signatures';
import { useEditor, useSlateSelector } from 'src/layout-engine/hooks';
import { makeStyles } from 'tss-react/mui';

type SignatureFieldFromQuery = NonNullable<
  Apollo.GetDocumentFieldsQueryHookResult['data']
>['document']['signatureFields'][number];

const useStyles = makeStyles()(theme => ({
  assigneeRow: {
    alignItems: 'center',
    backgroundColor: theme.appColors.fieldHeader,
    borderRadius: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
  },
  assigneeText: {
    fontWeight: 600,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  previewActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },

  previewTitle: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  signedPreview: {
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    height: defaultSignatureFieldHeightPx,
    marginBottom: theme.spacing(2),
    objectFit: 'contain',
    padding: theme.spacing(0.5),
    width: '100%',
  },
}));

interface SignatureFieldBodyProps {
  onDismiss: () => void;
  signatureField: SignatureFieldFromQuery;
}

// Picks Slate vs PDF body so each child only calls the hook it actually needs.
export function SignatureFieldBody(props: SignatureFieldBodyProps): JSX.Element | null {
  const { document } = useGetCurrentDoc();
  if (!document) return null;
  if (document.latestVersion.__typename === 'StaticFileVersion')
    return <PdfSignatureFieldBody {...props} />;
  return <SlateSignatureFieldBody {...props} />;
}

function SlateSignatureFieldBody(props: SignatureFieldBodyProps): JSX.Element | null {
  const ops = useSlateSignatureFieldOps(props.signatureField);
  return <SignatureFieldBodyShell {...props} ops={ops} />;
}

function PdfSignatureFieldBody(props: SignatureFieldBodyProps): JSX.Element | null {
  const ops = usePdfSignatureFieldOps(props.signatureField);
  return <SignatureFieldBodyShell {...props} ops={ops} />;
}

interface SignatureFieldOps {
  applyOrClear: (args: {
    adopted: AdoptedSignature;
    assigneeId: string;
    wasUnsigned: boolean;
  }) => Promise<void>;
  isReady: boolean;
  isSigned: boolean;
  isSignReady: boolean;
}

function SignatureFieldBodyShell({
  onDismiss,
  ops,
  signatureField,
}: SignatureFieldBodyProps & { ops: SignatureFieldOps }): JSX.Element | null {
  const { classes } = useStyles();
  const { signatureImg } = useGetUserSignatureImg();
  const { disabledReason } = useGetFieldDisabledReason(signatureField);

  const assigneeId = signatureField.action?.assignee?.id ?? null;
  const buttonDisabled = Boolean(disabledReason) || !ops.isReady || !assigneeId;
  /**
   * Block only the "Sign Here" action while its prerequisites are loading
   * (PDF: the cached signature bytes). "Clear Signature" needs nothing extra.
   */
  const signPrereqMissing = !ops.isSigned && !ops.isSignReady;
  const quickSignDisabled = buttonDisabled || signPrereqMissing;
  const quickSignDisabledReason =
    signPrereqMissing && !disabledReason ? 'Preparing your saved signature…' : disabledReason;

  async function applyAdoptedSignature(adopted: AdoptedSignature) {
    if (!ops.isReady || !assigneeId) return;
    const wasUnsigned = !ops.isSigned;
    onDismiss();
    await ops.applyOrClear({ adopted, assigneeId, wasUnsigned });
  }

  async function onClickSign() {
    if (!signatureImg) return;
    const cachedB64 = signatureB64Var().signature?.signatureB64 ?? '';
    /**
     * For Clear (already signed) we don't need the b64 bytes. For the Apply
     * path the button is disabled until `signatureB64Var` is populated by
     * `useSetSignatureB64`; this guard is defense-in-depth so a missing b64
     * can never stamp an empty (invisible) image onto a PDF.
     */
    if (!ops.isSigned && !cachedB64) return;
    await applyAdoptedSignature({ b64Url: cachedB64, id: signatureImg.id });
  }

  if (signatureImg && !ops.isSigned) {
    return (
      <SignaturePreviewBody
        disabled={quickSignDisabled}
        disabledReason={quickSignDisabledReason}
        previewActionsClassName={classes.previewActions}
        previewImgClassName={classes.signedPreview}
        signatureField={signatureField}
        signatureImgUrl={signatureImg.filestackUrl}
        titleClassName={classes.previewTitle}
        onContinueReviewing={onDismiss}
        onSignHere={onClickSign}
      />
    );
  }

  return (
    <>
      <SignatureAssigneeRow signatureField={signatureField} />
      {signatureImg ? (
        <QuickSignBody
          buttonDisabled={quickSignDisabled}
          disabledReason={quickSignDisabledReason}
          isSigned={ops.isSigned}
          signatureImgUrl={signatureImg.filestackUrl}
          signedPreviewClassName={classes.signedPreview}
          onClickSign={onClickSign}
        />
      ) : (
        <MobileAdoptAndSignBody
          buttonDisabled={buttonDisabled}
          disabledReason={disabledReason}
          onAdopted={applyAdoptedSignature}
        />
      )}
      <MobileSheetActionFooter onDismiss={onDismiss} />
    </>
  );
}

function SignaturePreviewBody({
  disabled,
  disabledReason,
  onContinueReviewing,
  onSignHere,
  previewActionsClassName,
  previewImgClassName,
  signatureField,
  signatureImgUrl,
  titleClassName,
}: {
  disabled: boolean;
  disabledReason: string;
  onContinueReviewing: () => void;
  onSignHere: (e: React.MouseEvent) => void;
  previewActionsClassName: string;
  previewImgClassName: string;
  signatureField: SignatureFieldFromQuery;
  signatureImgUrl: string;
  titleClassName: string;
}): JSX.Element {
  const { classes: actionClasses } = useMobileSheetActionButtonStyles();
  /**
   * The cached signature is already framed ("Signed via Aline" + brackets are
   * baked into the saved image), so show it as a plain img — running it back
   * through drawFramedSigPreview would double the frame.
   */
  return (
    <>
      <Typography
        className={titleClassName}
        data-cy={Select.document.editor.walkthrough.mobile.signaturePreviewBody}
      >
        Signature Preview
      </Typography>
      <SignatureAssigneeRow signatureField={signatureField} />
      <img alt="Your signature" className={previewImgClassName} src={signatureImgUrl} />
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div className={previewActionsClassName}>
          <AlineButton
            fullWidth
            buttonClassName={actionClasses.button}
            color="primary"
            variant="outlined"
            onClick={onContinueReviewing}
          >
            Continue Reviewing
          </AlineButton>
          <AlineButton
            fullWidth
            buttonClassName={actionClasses.button}
            color="primary"
            disabled={disabled}
            variant="contained"
            onClick={onSignHere}
          >
            Sign Here
          </AlineButton>
        </div>
      </AlineTooltip>
    </>
  );
}

function useSlateSignatureFieldOps(signatureField: SignatureFieldFromQuery): SignatureFieldOps {
  const editor = useEditor();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { nodeId } = signatureField;

  const { isSigned, sigFieldPath } = useSlateSelector(slateEditor => {
    for (const [node, path] of Editor.nodes(slateEditor, {
      at: [],
      match: n => isSignatureFieldElement(n) && n.fieldId === nodeId,
      voids: true,
    })) {
      return {
        isSigned: isSignatureFieldElement(node) && Boolean(node.signatureImgId),
        sigFieldPath: path,
      };
    }
    return { isSigned: false, sigFieldPath: null };
  }, equals);

  return {
    applyOrClear: async ({ adopted, assigneeId, wasUnsigned }) => {
      if (!sigFieldPath) return;
      AuthorizedTransforms.signOrClearSignatureField(editor, {
        assigneeId,
        sigFieldPath,
        signatureImgId: wasUnsigned ? adopted.id : null,
      });
    },
    isReady: Boolean(sigFieldPath),
    // Slate signs via `adopted.id`, never the b64 bytes — no extra prerequisite.
    isSignReady: true,
    isSigned,
  };
}

function usePdfSignatureFieldOps(signatureField: SignatureFieldFromQuery): SignatureFieldOps {
  const documentViewer = useMaybeDocumentViewer();
  const { tabId } = useRequiredEditorRuntime();
  const { syncAnnotations } = useSyncAnnotations(tabId);
  /**
   * Subscribing forces a re-render when PDFTron mutates its annotation list —
   * the widget lookup below reads mutable state React can't otherwise observe.
   */
  useEditorTabStateSelectorForTabId(tabId, state => state.viewer.annotationsChangedCount);
  /**
   * Re-render when the cached signature bytes land so the Sign button flips
   * from disabled to enabled without the user having to re-open the sheet.
   */
  const cachedSignature = useReactiveVar(signatureB64Var);

  const widget = documentViewer
    ? findPDFSignatureWidgetByFieldId(documentViewer, signatureField.nodeId)
    : null;
  const isSigned = Boolean(widget?.getAssociatedSignatureAnnotation());

  return {
    applyOrClear: async ({ adopted, wasUnsigned }) => {
      if (!documentViewer || !widget) return;
      if (wasUnsigned) {
        await applyPDFSignatureStamp({
          documentViewer,
          signatureB64: adopted.b64Url,
          signatureImgId: adopted.id,
          widget,
        });
      } else {
        clearPDFSignatureStamp(documentViewer, widget);
      }
      await syncAnnotations(documentViewer);
    },
    isReady: Boolean(widget),
    /**
     * The stamp needs the cached signature bytes; until `signatureB64Var` is
     * populated, signing would produce an empty (invisible) PDF stamp.
     */
    isSignReady: Boolean(cachedSignature.signature),
    isSigned,
  };
}

function SignatureAssigneeRow({
  signatureField,
}: {
  signatureField: SignatureFieldFromQuery;
}): JSX.Element {
  const { classes } = useStyles();
  const { avatarColor, primary } = getAssigneeDisplayDetails(
    signatureField.action?.assignee,
    signatureField.action?.assigneeRole
  );
  return (
    <div className={classes.assigneeRow}>
      <AlineAvatar
        avatarInfo={signatureField.action?.assignee || signatureField.action?.assigneeRole}
        styles={{ backgroundColor: avatarColor, size: 'sm' }}
      />
      <Typography noWrap className={classes.assigneeText}>
        {primary}
      </Typography>
    </div>
  );
}

function QuickSignBody({
  buttonDisabled,
  disabledReason,
  isSigned,
  onClickSign,
  signatureImgUrl,
  signedPreviewClassName,
}: {
  buttonDisabled: boolean;
  disabledReason: string;
  isSigned: boolean;
  onClickSign: (e: React.MouseEvent) => void;
  signatureImgUrl: string;
  signedPreviewClassName: string;
}): JSX.Element {
  const { classes: actionClasses } = useMobileSheetActionButtonStyles();
  return (
    <>
      <img alt="Your signature" className={signedPreviewClassName} src={signatureImgUrl} />
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineButton
            fullWidth
            buttonClassName={actionClasses.button}
            color="primary"
            disabled={buttonDisabled}
            variant={isSigned ? 'outlined' : 'contained'}
            onClick={onClickSign}
          >
            {isSigned ? 'Clear Signature' : 'Sign Here'}
          </AlineButton>
        </div>
      </AlineTooltip>
    </>
  );
}
